import { isDevToolsEnabled } from '../env';
import { useSessionStore } from '../stores/session/sessionStore';
import { clearDesktopHostMarker, isAcombaDesktopHost, markDesktopHostFromLocation } from './devToolsHost';
import {
    concealDevToolsPanel,
    mountDevToolsPanel,
    revealDevToolsPanel,
    unmountDevToolsPanel,
} from './panel/erudaPanel';
import {
    isDevToolsUnlocked,
    readUnlockRequestFromQuery,
    setDevToolsUnlocked,
    watchUnlockGesture,
    watchUnlockShortcut,
} from './devToolsUnlock';

/**
 * Wires up the in-app dev tools overlay. Called once from main.tsx, before the app renders.
 *
 * Two gates, both evaluated at runtime: the environment must be local or dev, and the app must be
 * running inside the Acomba desktop host. The environment gate can only be a runtime check —
 * `VITE_APP_ENV` arrives via `window.__ENV__`, injected at container startup, so a single built image
 * serves every environment and there is nothing to strip at build time.
 *
 * Everything observable sits behind the host gate — the panel, the `?devtools=` query parameter and both
 * unlock triggers alike. The triggers matter especially: the F12 handler cancels the event, and F12 is
 * page-cancellable in Chromium and Firefox, so a handler live in a plain browser would stop a developer's
 * real DevTools from opening on `local` and `dev`.
 *
 * The host gate is re-evaluated whenever the session kind changes, because the desktop launch marker is
 * only a boot-time stand-in: once a real session resolves, it either confirms the marker or disproves it.
 *
 * Returns a teardown that detaches the store subscription and the gesture listener, and disposes the
 * panel. The app never calls it — it exists so the wiring can be exercised without leaking listeners
 * between test cases.
 */
export function initDevTools(): () => void {
    if (!isDevToolsEnabled) return () => undefined;

    markDesktopHostFromLocation();

    const isInsideHost = (): boolean => isAcombaDesktopHost(useSessionStore.getState().sessionKind);

    const sync = (): void => {
        if (!isInsideHost()) {
            unmountDevToolsPanel();
            return;
        }

        mountDevToolsPanel()
            .then(() => {
                if (isDevToolsUnlocked()) revealDevToolsPanel();
                else concealDevToolsPanel();
            })
            .catch(() => {
                // The eruda chunk failed to load — a hashed chunk 404 after a dev redeploy is the usual
                // cause. Swallowed on purpose: this *is* the error-reporting tool, so there is nowhere to
                // report to, and the app must not care. erudaPanel drops the rejected promise, so the
                // next session change retries.
            });
    };

    const toggleUnlocked = (): void => {
        setDevToolsUnlocked(!isDevToolsUnlocked());
        sync();
    };

    if (isInsideHost()) {
        const requested = readUnlockRequestFromQuery();
        if (requested !== null) setDevToolsUnlocked(requested);
    }

    sync();

    // Only sessionKind is of interest. `session-meta` also carries the portal recovery flags, which the
    // cold-session path toggles repeatedly — syncing on those is wasted work.
    let lastSessionKind = useSessionStore.getState().sessionKind;
    const unsubscribe = useSessionStore.subscribe(() => {
        const { sessionKind } = useSessionStore.getState();
        if (sessionKind === lastSessionKind) return;
        lastSessionKind = sessionKind;

        // A session resolving to browser/portal *during this page's lifetime* is live evidence that the
        // launch marker no longer applies — drop it, so a plain browser dev session never inherits the
        // overlay. The value read at boot is not evidence: it hydrates from the previous session, and
        // covering the window before this one resolves is the whole point of the marker.
        if (sessionKind === 'browser' || sessionKind === 'portal') clearDesktopHostMarker();

        sync();
    });

    const stopWatchingGesture = watchUnlockGesture(toggleUnlocked, isInsideHost);
    const stopWatchingShortcut = watchUnlockShortcut(toggleUnlocked, isInsideHost);

    return () => {
        unsubscribe();
        stopWatchingGesture();
        stopWatchingShortcut();
        unmountDevToolsPanel();
    };
}
